import React, { useEffect, useState } from 'react';
import { ReactComponent as StormSpeed } from '../../assets/img/modal/storm_speed.svg';
import { useMap } from 'react-leaflet';
import ClipLoader from 'react-spinners/ClipLoader';
import { StormTracksService, Track } from '../../services/stormTracks/stormTracks';
import { useSearchParams } from 'react-router-dom';
import './StormTracksContent.css';

interface Location {
    timeForArrival: number;
    location: string;
}

interface TracksContent {
    type: string;
    title: string;
    date: string;
    maxRainRate: string;
    stormSpeed: string;
    locations: Location[];
}

interface StormTracksContentProps {
    position: [number, number],
        icon: any,
        zIndex: number,
        track: Track,
}

const LoadingSpinner: React.FC = () => (
    <div className="load-content">
        <ClipLoader
            color="#313638"
            loading={true}
            size={35}
            aria-label="Loading Spinner"
            data-testid="loader"
        />
    </div>
);

const sanitizeAndRenderHTML = (locations: Location[]) => {
    return locations.map((item, index) => {
        const sanitized = item.location
        .replace(/<(?!\/?(b|br)\s*\/?)[^>]+>/gi, '')
        .replace(/<\/b>/g, '</b> ');
        return (
            <div 
                key={`${index}-${item.timeForArrival}`}
                className='impact-item'
                dangerouslySetInnerHTML={{ __html: sanitized }} 
            />
        );
    })
};

const StormTracksContent = (data: StormTracksContentProps) => {
    const map = useMap();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [trackContent, setTrackContent] = useState<null | TracksContent>(null);
    const key = searchParams.get("key");
    const service = new StormTracksService(map, key);

    const init = async () => {
        const result: TracksContent = await service.getTrackContent(data.track);
        setTrackContent(result);
        setIsLoading(false);
    }

    useEffect(() => {
        init();
    }, []);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className='modal-content-wrapper'>
            <div className='storm-track-modal-infobox'>
                <div className='storm-track-modal-infobox-top'>
                    <img
                        src={data.icon.options.iconUrl}
                        alt="Storm Report"
                        width={50}
                        height={50}
                    />
                    <div>
                        <p className='storm-track-modal-infobox-heading'>{trackContent?.title}</p>
                        <div className='infobox-date'>
                            <p className='infobox-date-key'>Date:</p>
                            <p className='infobox-date-value'>{trackContent?.date}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className='storm-track-details'>
                <h2 className='storm-track-details-heading'>More Details</h2>
                {
                    trackContent?.maxRainRate && (
                        <div className='storm-track-details-content-flex'>
                            <p className='storm-track-details-content-title'>
                                Max Rain Rate:
                            </p>
                            <p className='storm-track-details-content-value-flex'>
                                {trackContent?.maxRainRate}
                            </p>
                        </div>
                    )
                }
                {
                    trackContent?.stormSpeed && (
                        <div className='storm-track-details-content'>
                            <div className='storm-track-details-content-title'>
                                <StormSpeed />
                                <p>Storm Speed:</p>
                            </div>
                            <p className='storm-track-details-content-value'>
                                {trackContent?.stormSpeed}
                            </p>
                        </div>
                    )
                }
                {
                    trackContent && trackContent?.locations.length! > 0 && (
                    <div className='storm-track-details-content'>
                        <div className='storm-track-details-content-title'>
                            <StormSpeed />
                            <p>Impact:</p>
                        </div>
                        <p className='storm-track-details-content-value'>
                            {sanitizeAndRenderHTML(trackContent?.locations)}
                        </p>
                    </div>
                    )
                }
            </div>

        </div>
    );
}

export default StormTracksContent;